import {
  Avatar,
  Box,
  Container,
  IconButton,
  Menu,
  Stack,
  Toolbar,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { MouseEvent, useState } from 'react';
import { Link as RouterLink, NavLink } from 'react-router-dom';
import type { User } from '../../entities/user';
import { useI18n } from '../../shared/i18n';
import { TranslatedText } from '../../shared/ui/TranslatedText';
import { ProfileCardPopover } from '../../widgets/profile-card-popover/ui/ProfileCardPopover';
import { getNavigationRoutes } from './appNavigation';
import { ThemeModeControl } from './ThemeModeControl';
import { InstallIcon } from './icons';

interface AppTopBarProps {
  installDisabled: boolean;
  onInstall: () => Promise<void>;
  user: User | null;
  onLogout: () => Promise<void>;
  onAuthorize: () => void;
}

const TopBar = styled('header')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 10,
  borderBottom: `1px solid ${theme.vars!.palette.surface.panelBorder}`,
  background: theme.vars!.palette.background.paper,
}));

const TopToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: 64,
  gap: theme.spacing(2),
}));

const BrandLink = styled(RouterLink)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  flexGrow: 1,
  color: theme.vars!.palette.text.primary,
  textDecoration: 'none',
}));

const BrandTitle = styled(TranslatedText)({
  fontWeight: 700,
});

const ActionGroup = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  borderRadius: theme.shape.surface,
  border: `1px solid ${theme.vars!.palette.surface.panelBorder}`,
  background: theme.vars!.palette.surface.soft,
}));

const ProfileAvatarSlot = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginLeft: theme.spacing(0.75),
  paddingLeft: theme.spacing(1),
  borderLeft: `1px solid ${theme.vars!.palette.surface.panelBorder}`,
}));

const NavGroup = styled('nav')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const TopNavLink = styled(NavLink)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 24,
  padding: 0,
  color: theme.vars!.palette.text.secondary,
  textDecoration: 'none',
  fontSize: theme.typography.body1.fontSize,
  fontWeight: 500,
  lineHeight: theme.typography.body1.lineHeight,
  textDecorationColor: 'transparent',
  textUnderlineOffset: '0.24em',
  transition: 'color 120ms ease, text-decoration-color 120ms ease',
  '&:hover': {
    color: theme.vars!.palette.primary.main,
    textDecoration: 'underline',
    textDecorationColor: theme.vars!.palette.primary.main,
  },
  '&.active': {
    color: theme.vars!.palette.primary.main,
    textDecoration: 'underline',
    textDecorationColor: theme.vars!.palette.primary.main,
  },
}));

const ProfileAvatar = styled(Avatar, {
  shouldForwardProp: (prop) => prop !== 'verified',
})<{ verified: boolean }>(({ theme, verified }) => ({
  width: 36,
  height: 36,
  border: `2px solid ${
    verified
      ? theme.vars!.palette.success.main
      : theme.vars!.palette.surface.panelBorder
  }`,
  background: theme.vars!.palette.surface.soft,
  color: theme.vars!.palette.text.primary,
  fontSize: 14,
  fontWeight: 700,
}));

export function AppTopBar({
  installDisabled,
  onInstall,
  user,
  onLogout,
  onAuthorize,
}: AppTopBarProps) {
  const { t } = useI18n();
  const topBarRoutes = getNavigationRoutes('topBar', Boolean(user));
  const [profileAnchor, setProfileAnchor] = useState<HTMLElement | null>(null);

  const profileOpen = Boolean(profileAnchor);
  const handleProfileOpen = (event: MouseEvent<HTMLElement>) => {
    setProfileAnchor(event.currentTarget);
  };

  const handleProfileClose = () => {
    setProfileAnchor(null);
  };

  const handleAuthorize = () => {
    handleProfileClose();
    onAuthorize();
  };

  const handleInstallClick = () => {
    void onInstall();
  };

  const handleLogout = async () => {
    handleProfileClose();
    await onLogout();
  };

  return (
    <TopBar>
      <Container maxWidth="lg">
        <TopToolbar disableGutters>
          <BrandLink to="/">
            <BrandTitle messageKey="app.title" />
          </BrandLink>
          <NavGroup aria-label="Primary page navigation">
            {topBarRoutes.map(({ path, labelKey, navMatchEnd }) => (
              <TopNavLink key={path} to={path} end={navMatchEnd}>
                {labelKey ? t(labelKey) : path}
              </TopNavLink>
            ))}
          </NavGroup>
          <ActionGroup data-testid="topbar-action-group">
            <Tooltip title={t('app.actions.install')}>
              <span>
                <IconButton
                  color="inherit"
                  onClick={handleInstallClick}
                  disabled={installDisabled}
                >
                  <InstallIcon />
                </IconButton>
              </span>
            </Tooltip>
            <ThemeModeControl />
          </ActionGroup>
          <ProfileAvatarSlot>
            <Tooltip title={t('app.profile.open')}>
              <IconButton
                color="inherit"
                onClick={handleProfileOpen}
                aria-label={t('app.profile.open')}
                data-testid="topbar-profile-avatar-button"
              >
                <ProfileAvatar
                  src={user?.avatarUrl}
                  verified={Boolean(user?.isVerified)}
                />
              </IconButton>
            </Tooltip>
          </ProfileAvatarSlot>
        </TopToolbar>
      </Container>
      <Menu
        anchorEl={profileAnchor}
        open={profileOpen}
        onClose={handleProfileClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              minWidth: 320,
            },
          },
        }}
      >
        <ProfileCardPopover
          user={user}
          onAuthorize={handleAuthorize}
          onLogout={handleLogout}
        />
      </Menu>
    </TopBar>
  );
}
